import { type ComputedRef, computed, onScopeDispose, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { IMPERSONATION_COOKIE_NAME } from "@/constants";
import type { ResolvedImpersonation } from "@/providers/impersonation/types";
import { useResolveImpersonation } from "@/providers/impersonation/useResolveImpersonation";
import { readCookie, subscribeToCookie } from "@/util/cookieMonitor";

/**
 * The impersonation session.
 *
 * The `impersonation_token` cookie is the single source of truth (TRD §3.3), but it has two kinds
 * of writer: this app, and everyone else (AIQ on the shared domain, another tab, expiry). This
 * composable is the one place those are reconciled, so components never coordinate with each other
 * about it.
 *
 * State is module-scoped rather than per-caller: the session is global, and a component that
 * unmounts must not take it with them.
 */
const token = ref<string | undefined>(readCookie(IMPERSONATION_COOKIE_NAME));
const isExiting = ref<boolean>(false);

export type ImpersonationSession = {
  token: ComputedRef<string | undefined>;
  profile: ComputedRef<ResolvedImpersonation["impersonated_profile"] | undefined>;
  isImpersonating: ComputedRef<boolean>;
  isExiting: ComputedRef<boolean>;
  exit: () => Promise<void>;
};

export const useImpersonationSession = (): ImpersonationSession => {
  const store = useStore();
  const router = useRouter();

  const unsubscribe = subscribeToCookie(IMPERSONATION_COOKIE_NAME, (next, origin) => {
    token.value = next;

    if (origin === "external") store.dispatch("refreshUserDataFromServer");
  });

  onScopeDispose(unsubscribe);

  const { data: resolved } = useResolveImpersonation({
    token,
    useQueryOptions: {
      enabled: computed(() => Boolean(token.value)),
      retry: false,
      staleTime: Number.POSITIVE_INFINITY,
    },
  });

  const profile = computed(() => resolved.value?.impersonated_profile);

  return {
    token: computed(() => token.value),
    profile,
    isImpersonating: computed<boolean>(() => Boolean(token.value) && Boolean(profile.value)),
    isExiting: computed<boolean>(() => isExiting.value),

    exit: async (): Promise<void> => {
      if (isExiting.value) return;

      isExiting.value = true;

      try {
        // The action clears the cookie through `clearCookie`, which announces the write, so the
        // subscription above converges `token` without treating it as someone else's change.
        await store.dispatch("exitImpersonation");

        const returnUrl = sessionStorage.getItem("impersonationModeReturnUrl");
        await router.push(returnUrl ?? { name: "impersonation" });
      } finally {
        isExiting.value = false;
      }
    },
  };
};
